import 'styled-components';

import { Colors, Spacing } from 'constants/ui';

/**
 * Globally `1rem = 10px` (set via `html { font-size: 62.5% }` in global.css).
 * So `12px` → `"1.2rem"`, `16px` → `"1.6rem"`, etc.
 */
export const px = (value: number): string => `${value / 10}rem`;

const spacing = {
  xs: px(Spacing.xs),
  sm: px(Spacing.sm),
  md: px(Spacing.md),
  lg: px(Spacing.lg),
  xl: px(Spacing.xl),
  xxl: px(Spacing.xxl),
};

const darkColors: typeof Colors = {
  accent: '#38BDF8',
  accentDark: '#0EA5E9',
  accentLight: '#0C4A6E',
  accentBorder: '#075985',
  accentBgSoft: '#0C2937',
  accentBorderSoft: '#0EA5E9',
  accentTextDark: '#7DD3FC',

  darkSlate: '#F1F5F9',
  slate: '#CBD5E1',
  steelGrey: '#94A3B8',
  slateGrey: '#94A3B8',
  slateMuted: '#64748B',
  slateLight: '#94A3B8',

  ink: '#F1F5F9',
  inkPanelBg: '#1E293B',
  inkPanelSurface: '#1F2937',
  inkPanelBorder: '#334155',

  avatarPinkBg: '#F9D2E4',
  avatarPinkFg: '#D9629B',
  avatarSandBg: '#F5E6C4',
  avatarSandFg: '#B8912F',

  white: '#0F172A',
  black: '#000000',
  border: '#334155',
  borderStrong: '#475569',
  surface: '#1E293B',
  surfaceSoft: '#1E293B',

  overlayDark: 'rgba(0, 0, 0, 0.5)',
  overlayDarker: 'rgba(0, 0, 0, 0.6)',
  overlayLight: 'rgba(255, 255, 255, 0.3)',
  overlayText: '#FFFFFF',
  inkPanelMutedText: '#9AA5B4',
  shadowSoft: 'rgba(0, 0, 0, 0.6)',
  shadowMenu: 'rgba(0, 0, 0, 0.5)',

  amber: '#FBBF24',
  amberBg: '#451A03',
  amberBorder: '#92400E',
  amberText: '#FDE68A',
  amberBgSoft: '#78350F',
  amberSolid: '#FCD34D',

  teal: '#2DD4BF',
  tealBgSoft: 'rgba(45, 212, 191, 0.12)',

  greenCta: '#34D399',

  success: '#22C55E',
  successBright: '#22C55E',
  successDark: '#16A34A',
  successDarkest: '#15803D',
  successText: '#86EFAC',
  successBg: '#052E16',
  successBgSoft: '#14532D',
  successBorder: '#166534',
  successBorderSoft: '#15803D',

  info: '#60A5FA',
  infoBg: '#1E3A8A',
  infoBorder: '#1E40AF',

  purpleText: '#C4B5FD',
  purpleBgSoft: '#2E1065',
  purpleBorder: '#5B21B6',

  errorRed: '#F87171',
  error: '#EF4444',
  errorDark: '#DC2626',
  errorDarkest: '#B91C1C',
  errorBg: '#450A0A',
  errorBorder: '#7F1D1D',

  disabled: '#475569',

  trustBadgeGoldBg: 'rgba(251, 191, 36, 0.16)',
  trustBadgeGoldFg: '#FDE68A',
  trustBadgeGoldBorder: 'rgba(245, 158, 11, 0.5)',
  trustBadgePurpleBg: 'rgba(139, 92, 246, 0.18)',
  trustBadgePurpleFg: '#C4B5FD',
  trustBadgePurpleBorder: 'rgba(139, 92, 246, 0.5)',
  trustBadgeGreenBg: 'rgba(16, 185, 129, 0.18)',
  trustBadgeGreenFg: '#A7F3D0',
  trustBadgeGreenBorder: 'rgba(16, 185, 129, 0.5)',
  trustBadgeBlueBg: 'rgba(14, 165, 233, 0.18)',
  trustBadgeBlueFg: '#BAE6FD',
  trustBadgeBlueBorder: 'rgba(14, 165, 233, 0.5)',
};

export const lightTheme = {
  colors: Colors,
  spacing,
};

export const darkTheme = {
  colors: darkColors,
  spacing,
};

export type ColorMode = 'light' | 'dark';
export type Theme = typeof lightTheme;

export const themeFor = (mode: ColorMode): Theme =>
  mode === 'dark' ? darkTheme : lightTheme;

export const theme = lightTheme;

declare module 'styled-components' {
  // eslint-disable-next-line @typescript-eslint/no-empty-interface
  export interface DefaultTheme extends Theme {}
}
